import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useOutletContext, useParams } from 'react-router';
import Alert from 'react-bootstrap/Alert';
import ListGroup from 'react-bootstrap/ListGroup';
import Modal from 'react-bootstrap/Modal';
import type { RouteHandle } from '../types';
import { PageId } from '../types';
import './SearchPage.scss';
import FlashcardView from '@app/components/FlashcardView';
import SearchPanel from '@app/components/SearchPanel';
import { useAtomValue, useSetAtom } from 'jotai';
import { canLoadMoreWords, getWordsDataAtomDefaults, wordsChunkLoadingAtom, wordsDataAtom, wordsErrorAtom, wordsLoadMoreAtom, wordsLoadingAtom } from '@app/atoms/wordsAtoms';
import { loadingShowIdsAtom } from '@app/atoms/uiAtoms';
import Button from 'react-bootstrap/Button';
import WordsListView from '@app/components/WordsListView';
import { selectedEpisodeAtom, selectedMediaAtom, selectedSeasonAtom } from '@app/atoms/selectedMediaAtoms';
import { getEpisodeProgress, getProgress, saveProgress } from '@app/utils/api';
import { buildShowIdentifier, collectShowIdentifiers, isYoutubeShow } from '@shared/utils/show';
import { LastWatchedExt } from '@shared/types';
import MediaInfo from '@app/components/MediaInfo';
import { getProgressPercent, isProgressLoading } from '@app/utils/progress';
import Spinner from 'react-bootstrap/Spinner';

const SearchPage = () => {
  const wordsData = useAtomValue(wordsDataAtom);
  const wordsLoading = useAtomValue(wordsLoadingAtom);
  const wordsChunkLoading = useAtomValue(wordsChunkLoadingAtom);
  const loadingShowIds = useAtomValue(loadingShowIdsAtom);
  const wordsError = useAtomValue(wordsErrorAtom);
  const selectedMedia = useAtomValue(selectedMediaAtom);
  const selectedSeason = useAtomValue(selectedSeasonAtom);
  const selectedEpisode = useAtomValue(selectedEpisodeAtom);
  const [showCards, setShowCards] = useState(false);
  const [showWordsList, setShowWordsList] = useState(false);
  const [hasEpisodeProgress, setHasEpisodeProgress] = useState(false);
  const [hasUnstudiedCards, setHasUnstudiedCards] = useState(false);
  const [hasRememberedCards, setHasRememberedCards] = useState(false);
  const [isEpisodeProgressLoading, setIsEpisodeProgressLoading] = useState(false);
  const [episodeProgress, setEpisodeProgress] = useState({
    total: 0,
    remembered: 0,
    notRemembered: 0,
    completionPercent: 0,
  });
  const [episodeCardStats, setEpisodeCardStats] = useState({
    answered: 0,
    remembered: 0,
    notRemembered: 0,
  });
  const [progressRefreshSeq, setProgressRefreshSeq] = useState(0);
  const [flashcardMode, setFlashcardMode] = useState<'main' | 'unstudied' | 'remembered'>('main');
  const [recentYoutubeItems, setRecentYoutubeItems] = useState<LastWatchedExt[]>([]);
  const [isResettingProgress, setIsResettingProgress] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showLoadMoreConfirm, setShowLoadMoreConfirm] = useState(false);
  const [loadedCardsTotal, setLoadedCardsTotal] = useState(0);
  const [activeCardsLimit, setActiveCardsLimit] = useState(0);
  const [isSelectedShowLoading, setIsSelectedShowLoading] = useState(false);
  const deferredRefreshTimerRef = useRef<number | null>(null);
  const lastEpisodeProgressKeyRef = useRef<string>('');
  const requestLoadMoreWords = useSetAtom(wordsLoadMoreAtom);
  const setSelectedMedia = useSetAtom(selectedMediaAtom);
  const setSelectedSeason = useSetAtom(selectedSeasonAtom);
  const setSelectedEpisode = useSetAtom(selectedEpisodeAtom);
  const setWordsData = useSetAtom(wordsDataAtom);
  const setWordsError = useSetAtom(wordsErrorAtom);
  const { showKey } = useParams();
  const routeShowKey = showKey ? decodeURIComponent(showKey) : '';
  const navigate = useNavigate();
  const location = useLocation();

  const { pageId } = useOutletContext<RouteHandle>();

  useEffect(() => {
    if (pageId !== PageId.YOUTUBE || showKey) {
      return;
    }

    setSelectedMedia(null);
    setSelectedSeason(null);
    setSelectedEpisode(null);
    setWordsData(getWordsDataAtomDefaults());
    setWordsError('');
  }, [pageId, showKey, setSelectedMedia, setSelectedSeason, setSelectedEpisode, setWordsData, setWordsError]);

  useEffect(() => {
    if (pageId !== PageId.YOUTUBE || showKey) {
      setRecentYoutubeItems([]);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const progress = await getProgress(1, 30, { includeHidden: true });
        if (cancelled) {
          return;
        }
        const youtubeOnly = (progress.list || []).filter((item) => isYoutubeShow(item.show));
        setRecentYoutubeItems(youtubeOnly.slice(0, 8));
      } catch {
        if (!cancelled) {
          setRecentYoutubeItems([]);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [pageId, showKey, progressRefreshSeq]);

  useEffect(() => {
    if (!showKey) {
      return;
    }

    const decodedShowKey = decodeURIComponent(showKey);

    setShowCards(false);
    setShowWordsList(false);
    setFlashcardMode('main');
    setSelectedMedia(null);
    setSelectedSeason(null);
    setSelectedEpisode(null);
    setWordsData(getWordsDataAtomDefaults());
    setWordsError('');

    let cancelled = false;
    (async () => {
      try {
        const isYoutubePage = pageId === PageId.YOUTUBE;
        const stateSeed = (location.state as { seedMedia?: any } | null)?.seedMedia || null;
        const progress = isYoutubePage
          ? await getProgress(1, 1, {
            screenmediaId: decodedShowKey,
            includeHidden: true,
          })
          : await getProgress(1, 1, {
            showId: decodedShowKey,
            includeHidden: true,
          });
        const resolvedProgress = (isYoutubePage && !progress.list?.length)
          ? await getProgress(1, 1, {
            showId: decodedShowKey,
            includeHidden: true,
          })
          : progress;
        if (cancelled) {
          return;
        }

        const item = resolvedProgress.list?.[0];
        if (!item?.show) {
          if (stateSeed) {
            setSelectedMedia(stateSeed);
            if (stateSeed?.seasons?.length) {
              setSelectedSeason(stateSeed.seasons[0]);
              setSelectedEpisode(null);
            } else {
              setSelectedSeason(null);
              setSelectedEpisode(null);
            }
          } else {
            setSelectedMedia(null);
            setSelectedSeason(null);
            setSelectedEpisode(null);
          }
          return;
        }

        const show: any = {
          ...(item.show as any),
          dbId: (item.show as any)?.dbId || item.screenmediaId,
        };
        if (isYoutubePage && show.dbId) {
          // Keep a stable identifier for YouTube flow, even if legacy progress rows stored show as "movie".
          if (!show.title) {
            show.title = `yt:${show.dbId}`;
          }
          show.originalTitle = show.originalTitle || `YouTube: ${show.dbId}`;
        }
        if (!isYoutubePage && isYoutubeShow(show)) {
          const youtubeKey = show.dbId || decodedShowKey;
          navigate(`/youtube/${encodeURIComponent(youtubeKey)}`, { replace: true });
          return;
        }

        setSelectedMedia(show);

        let nextSeason = null;
        let nextEpisode = null;

        const forceSeasonPicker = !!(location.state as { forceSeasonPicker?: boolean } | null)?.forceSeasonPicker;
        if (!forceSeasonPicker && item.season != null && show?.seasons) {
          nextSeason = show.seasons.find((s: any) => s.num === item.season) || null;
          if (nextSeason && item.episode != null) {
            nextEpisode = nextSeason.episodes.find((e: any) => e.num === item.episode) || null;
          }
        }

        setSelectedSeason(nextSeason);
        setSelectedEpisode(nextEpisode);
      } catch {
        if (!cancelled) {
          setSelectedMedia(null);
          setSelectedSeason(null);
          setSelectedEpisode(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [showKey, pageId, location.state, navigate, setSelectedMedia, setSelectedSeason, setSelectedEpisode, setWordsData, setWordsError]);

  useEffect(() => {
    if (wordsData.words.length === 0) {
      setShowCards(false);
    }
  }, [wordsData.words.length]);

  useEffect(() => {
    if (!wordsData.words.length) {
      return;
    }

    setLoadedCardsTotal((prev) => (prev >= wordsData.words.length ? prev : wordsData.words.length));
  }, [wordsData.words.length]);

  useEffect(() => {
    if (!selectedMedia || !wordsData.words.length) {
      setIsEpisodeProgressLoading(false);
      setIsSelectedShowLoading(false);
      lastEpisodeProgressKeyRef.current = '';
      setHasEpisodeProgress(false);
      setHasUnstudiedCards(false);
      setHasRememberedCards(false);
      setEpisodeProgress({
        total: 0,
        remembered: 0,
        notRemembered: 0,
        completionPercent: 0,
      });
      setLoadedCardsTotal(0);
      setActiveCardsLimit(0);
      setEpisodeCardStats({
        answered: 0,
        remembered: 0,
        notRemembered: 0,
      });
      return;
    }

    const showId = buildShowIdentifier(selectedMedia);
    if (!showId) {
      setIsEpisodeProgressLoading(false);
      setIsSelectedShowLoading(false);
      lastEpisodeProgressKeyRef.current = '';
      setHasEpisodeProgress(false);
      setHasUnstudiedCards(false);
      setHasRememberedCards(false);
      setEpisodeProgress({
        total: 0,
        remembered: 0,
        notRemembered: 0,
        completionPercent: 0,
      });
      setEpisodeCardStats({
        answered: 0,
        remembered: 0,
        notRemembered: 0,
      });
      return;
    }

    let cancelled = false;
    const progressScopeKey = `${pageId}|${showId}|${selectedSeason?.num ?? ''}|${selectedEpisode?.num ?? ''}`;
    const isNewProgressScope = lastEpisodeProgressKeyRef.current !== progressScopeKey;
    if (isNewProgressScope) {
      lastEpisodeProgressKeyRef.current = progressScopeKey;
      setIsEpisodeProgressLoading(true);
    }
    (async () => {
      try {
        const isYoutubePage = pageId === PageId.YOUTUBE;
        const [progress, episodeProgressData] = await Promise.all([
          getProgress(1, 1, {
            showId,
            screenmediaId: isYoutubePage ? (selectedMedia.dbId || showId) : undefined,
            season: selectedSeason?.num,
            episode: selectedEpisode?.num,
            includeHidden: true,
          }),
          getEpisodeProgress({
            showId,
            screenmediaId: isYoutubePage ? (selectedMedia.dbId || showId) : undefined,
            season: selectedSeason?.num,
            episode: selectedEpisode?.num,
          })
        ]);
        if (cancelled) {
          return;
        }
        const currentCardIndex = progress.list[0]?.details?.currentCardIndex ?? 0;
        const cardStatuses = progress.list[0]?.details?.cardStatuses ?? {};
        const loadedFromProgress = Number(progress.list[0]?.details?.loadedCardsTotal) || 0;
        const activeFromProgress = Number(progress.list[0]?.details?.activeCardsLimit) || 0;
        setIsSelectedShowLoading(isProgressLoading(progress.list?.[0]));
        const statusValues = Object.values(cardStatuses);
        const answeredCards = statusValues.length;
        const rememberedCards = statusValues.filter((status) => status === 'remembered').length;
        const notRememberedCards = statusValues.filter((status) => status === 'not_remembered').length;
        const hasAnyCardStatuses = Object.keys(cardStatuses).length > 0;
        setHasEpisodeProgress(currentCardIndex > 0 || hasAnyCardStatuses);
        setHasUnstudiedCards(
          statusValues.some((status) => status === 'not_remembered')
        );
        setHasRememberedCards(
          statusValues.some((status) => status === 'remembered')
          || (episodeProgressData.remembered > 0)
        );
        setLoadedCardsTotal(loadedFromProgress);
        setActiveCardsLimit(activeFromProgress);
        setEpisodeCardStats({
          answered: answeredCards,
          remembered: rememberedCards,
          notRemembered: notRememberedCards,
        });
        setEpisodeProgress(episodeProgressData);
      } catch {
        if (!cancelled) {
          setIsSelectedShowLoading(false);
          setHasEpisodeProgress(false);
          setHasUnstudiedCards(false);
          setHasRememberedCards(false);
          setEpisodeProgress({
            total: 0,
            remembered: 0,
            notRemembered: 0,
            completionPercent: 0,
          });
          setLoadedCardsTotal(0);
          setActiveCardsLimit(0);
          setEpisodeCardStats({
            answered: 0,
            remembered: 0,
            notRemembered: 0,
          });
        }
      } finally {
        if (!cancelled && isNewProgressScope) {
          setIsEpisodeProgressLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [pageId, selectedMedia, selectedSeason?.num, selectedEpisode?.num, wordsData.words.length, progressRefreshSeq]);

  useEffect(() => {
    if (!selectedMedia || !isSelectedShowLoading) {
      return;
    }
    const timer = window.setInterval(() => {
      setProgressRefreshSeq((prev) => prev + 1);
    }, 3000);

    return () => {
      window.clearInterval(timer);
    };
  }, [selectedMedia, isSelectedShowLoading]);

  const handleCardsOpen = () => {
    if (wordsData.words.length > 0) {
      setFlashcardMode('main');
      setShowCards(true);
    }
  };

  const handleUnstudiedCardsOpen = () => {
    if (wordsData.words.length > 0) {
      setFlashcardMode('unstudied');
      setShowCards(true);
    }
  };

  const handleRememberedCardsOpen = () => {
    if (wordsData.words.length > 0) {
      setFlashcardMode('remembered');
      setShowCards(true);
    }
  };

  const handleCardsClose = () => {
    setShowCards(false);
    setProgressRefreshSeq((prev) => prev + 1);
    if (deferredRefreshTimerRef.current) {
      clearTimeout(deferredRefreshTimerRef.current);
    }
    deferredRefreshTimerRef.current = window.setTimeout(() => {
      setProgressRefreshSeq((prev) => prev + 1);
      deferredRefreshTimerRef.current = null;
    }, 250);
  };

  const handleListOpen = () => {
    setShowWordsList(true);
  };

  const handleWordsListClose = () => {
    setShowWordsList(false);
  };

  const handleLoadMoreWords = () => {
    const selectedShowIds = collectShowIdentifiers(selectedMedia, [routeShowKey]);
    const blockedByAnotherLoading = loadingShowIds.size > 0
      && (!selectedShowIds.length || !selectedShowIds.some((id) => loadingShowIds.has(id)));
    if (blockedByAnotherLoading) {
      return;
    }
    if (previewPending || wordsLoading || isBackgroundChunkLoading || !hasMoreWords) {
      return;
    }
    setShowLoadMoreConfirm(true);
  };

  const handleConfirmLoadMoreWords = () => {
    setShowLoadMoreConfirm(false);
    requestLoadMoreWords();
  };

  const handleResetProgress = async () => {
    if (!selectedMedia || isResettingProgress) {
      return;
    }
    setIsResettingProgress(true);
    try {
      const firstPart = wordsData.parts.get(1);
      const firstBatchWords = firstPart?.words ? [...firstPart.words] : [];
      const baselineTarget = Math.max(1, Math.min(30, previewTarget || 30));
      const resetDeckWords = wordsData.words.length >= baselineTarget
        ? wordsData.words.slice(0, baselineTarget)
        : firstBatchWords.slice(0, baselineTarget);
      const resetLoadedTotal = Math.max(
        1,
        Math.min(
          baselineTarget,
          resetDeckWords.length || firstBatchWords.length || previewTarget || firstPartWordsLength || baselineTarget
        )
      );

      await saveProgress({
        show: selectedMedia,
        season: selectedSeason?.num,
        episode: selectedEpisode?.num,
        screenmediaId: selectedMedia.dbId,
        details: {
          currentCardIndex: 0,
          cardStatuses: null,
          loadedCardsTotal: resetLoadedTotal,
          activeCardsLimit: resetLoadedTotal,
        },
      });

      if (firstPart) {
        const resetWordIds = new Set(resetDeckWords.map((word) => word.id));
        const sortedParts = Array.from(wordsData.parts.entries())
          .sort((a, b) => a[0] - b[0]);
        let nextPartToLoad = 0;
        for (const [partNumber, partData] of sortedParts) {
          if (partNumber <= 1) {
            continue;
          }
          const hasUnseenWords = (partData.words || []).some((word) => !resetWordIds.has(word.id));
          if (hasUnseenWords) {
            nextPartToLoad = partNumber;
            break;
          }
        }
        if (!nextPartToLoad && wordsData.totalParts > 1) {
          nextPartToLoad = 2;
        }

        const syntheticFirstPart: any = {
          ...(firstPart as any),
          part: 1,
          words: resetDeckWords.length ? resetDeckWords : firstBatchWords,
          nextPart: nextPartToLoad || undefined,
          previewPending: false,
          previewTargetSize: previewTarget || 0,
          previewLoadedSize: resetDeckWords.length || firstBatchWords.length,
        };

        setWordsData((prev) => ({
          words: resetDeckWords.length ? resetDeckWords : firstBatchWords,
          parts: new Map([[1, syntheticFirstPart]]),
          totalParts: prev.totalParts,
          nextPartToLoad,
          previewPending: false,
          previewTargetSize: prev.previewTargetSize,
          previewLoadedSize: resetDeckWords.length || firstBatchWords.length,
        }));
      }

      setLoadedCardsTotal(resetLoadedTotal);
      setActiveCardsLimit(resetLoadedTotal);
      setProgressRefreshSeq((prev) => prev + 1);
      setShowResetConfirm(false);
    } catch (error) {
      console.error('Failed to reset progress:', error);
    } finally {
      setIsResettingProgress(false);
    }
  };

  const openYoutubeHistoryItem = (item: LastWatchedExt) => {
    const show: any = {
      ...(item.show as any),
      dbId: (item.show as any)?.dbId || item.screenmediaId,
    };
    if (show.dbId) {
      show.title = typeof show.title === 'string' && show.title.startsWith('yt:')
        ? show.title
        : `yt:${show.dbId}`;
      show.originalTitle = show.originalTitle || `YouTube: ${show.dbId}`;
    }
    setSelectedMedia(show);
    setSelectedSeason(null);
    setSelectedEpisode(null);
    const showId = buildShowIdentifier(show);
    if (!showId) {
      return;
    }
    navigate(`/youtube/${encodeURIComponent(showId)}`);
  };

  useEffect(() => {
    return () => {
      if (deferredRefreshTimerRef.current) {
        clearTimeout(deferredRefreshTimerRef.current);
      }
    };
  }, []);

  const handleSetActiveCardsLimit = async (limit: number) => {
    if (!selectedMedia) {
      return;
    }
    const safeLoaded = Math.max(loadedCardsTotal, wordsData.words.length, firstPartWordsLength);
    const safeLimit = Math.max(1, Math.min(limit, safeLoaded));
    if (safeLimit === (activeCardsLimit || Math.min(30, safeLoaded))) {
      return;
    }
    try {
      await saveProgress({
        show: selectedMedia,
        season: selectedSeason?.num,
        episode: selectedEpisode?.num,
        screenmediaId: selectedMedia.dbId,
        details: {
          activeCardsLimit: safeLimit,
        },
      });
      setProgressRefreshSeq((prev) => prev + 1);
    } catch (error) {
      console.error('Failed to set active cards limit:', error);
    }
  };

  const firstPartWordsLength = (wordsData.parts.get(1)?.words?.length ?? 0);
  const previewTarget = wordsData.previewTargetSize || 0;
  const previewReady = wordsData.previewLoadedSize || wordsData.words.length;
  const previewPending = !!wordsData.previewPending;
  const selectedShowIds = collectShowIdentifiers(selectedMedia, [routeShowKey]);
  const isSeriesLoadingGlobal = (selectedShowIds.some((id) => loadingShowIds.has(id))) || isSelectedShowLoading;
  const isChunkLoadingLocal = !wordsLoading
    && wordsChunkLoading
    && wordsData.words.length > 0
    && !previewPending;
  const isBackgroundChunkLoading = !wordsLoading
    && wordsData.words.length > 0
    && !previewPending
    && (isSeriesLoadingGlobal || isChunkLoadingLocal);
  const visiblePartsCount = wordsData.parts.size;
  const nextChunkPart = Math.max(1, wordsData.nextPartToLoad || (visiblePartsCount + 1));
  const effectiveLoadedCardsTotal = loadedCardsTotal > 0
    ? loadedCardsTotal
    : Math.max(wordsData.words.length, firstPartWordsLength, previewTarget);
  const isDeckHydratedToLoaded = wordsData.words.length >= effectiveLoadedCardsTotal;
  const effectiveActiveCardsLimit = Math.max(
    0,
    Math.min(
      activeCardsLimit || episodeProgress.total || Math.min(30, effectiveLoadedCardsTotal || 30),
      effectiveLoadedCardsTotal || episodeProgress.total || 30
    )
  );
  const episodeTotal = Math.max(
    episodeProgress.total || 0,
    effectiveActiveCardsLimit || 0
  );
  const episodeRemembered = Math.min(episodeCardStats.remembered, episodeTotal);
  const episodeNotRemembered = Math.min(episodeCardStats.notRemembered, episodeTotal);
  const episodeAttempted = Math.min(episodeTotal, episodeCardStats.answered);
  const episodeUnseen = Math.max(0, episodeTotal - episodeAttempted);
  const episodeCompletionPercent = episodeTotal ? Math.round((episodeRemembered / episodeTotal) * 100) : 0;
  const episodeReviewPercent = episodeTotal ? Math.round((episodeNotRemembered / episodeTotal) * 100) : 0;
  const episodeUnseenPercent = Math.max(0, 100 - episodeCompletionPercent - episodeReviewPercent);
  const hasMoreWords = canLoadMoreWords(wordsData);
  const showLoadMoreButton = !isEpisodeProgressLoading
    && (hasMoreWords || isBackgroundChunkLoading)
    && (isDeckHydratedToLoaded || isBackgroundChunkLoading);
  const compactChunkLoadingLabel = wordsData.totalParts > 0
    ? `Chunk ${Math.min(nextChunkPart, wordsData.totalParts)} of ${wordsData.totalParts} is loading`
    : 'Loading next chunk';
  const materialTypeLabel = pageId === PageId.YOUTUBE
    ? 'video'
    : selectedMedia?.type === 'movie'
      ? 'movie'
      : 'episode';
  const allWordsReviewed = episodeTotal > 0 && episodeAttempted >= episodeTotal;
  const allWordsLearned = allWordsReviewed && episodeNotRemembered === 0;
  const progressStatusLabel = allWordsLearned
    ? 'Ready to watch'
    : allWordsReviewed
      ? 'Needs review'
      : 'In progress';
  const progressStatusClass = allWordsLearned
    ? 'progress-chip--success'
    : allWordsReviewed
      ? 'progress-chip--warning'
      : 'progress-chip--info';
  const primaryCtaTitle = !hasEpisodeProgress
    ? 'Start here'
    : allWordsReviewed
      ? 'Review here'
      : 'Continue here';
  const primaryCtaHint = !hasEpisodeProgress
    ? 'Open cards to start learning'
    : allWordsReviewed
      ? 'You reviewed all cards, choose a review pass'
      : 'Resume your card session first';
  const primaryCtaLabel = !hasEpisodeProgress
    ? 'Start learning cards'
    : allWordsReviewed
      ? (hasUnstudiedCards ? 'Review unstudied cards' : 'Review cards')
      : 'Continue learning cards';
  const handlePrimaryAction = () => {
    if (allWordsReviewed && !previewPending) {
      if (hasUnstudiedCards) {
        handleUnstudiedCardsOpen();
        return;
      }
      if (hasRememberedCards) {
        handleRememberedCardsOpen();
        return;
      }
    }
    handleCardsOpen();
  };
  const forceSeasonPickerFromState = !!(location.state as { forceSeasonPicker?: boolean } | null)?.forceSeasonPicker;

  return (
    <div className="container">
      {pageId && <SearchPanel pageId={pageId} forceSeasonPicker={forceSeasonPickerFromState} />}
      {pageId === PageId.YOUTUBE && !showKey && recentYoutubeItems.length > 0 && (
        <div className="episode-progress-panel border rounded p-3 mt-3">
          <div className="fw-semibold mb-2">Recently viewed YouTube videos</div>
          <ListGroup>
            {recentYoutubeItems.map((item) => (
              <ListGroup.Item key={item.id} className="p-0 border-0 mb-2">
                <Button
                  variant="outline-secondary"
                  className="d-flex w-100"
                  onClick={() => openYoutubeHistoryItem(item)}
                >
                  <MediaInfo media={item.show as any} progressPercent={getProgressPercent(item)} />
                </Button>
              </ListGroup.Item>
            ))}
          </ListGroup>
        </div>
      )}
      {/* Error Alert */}
      {wordsError && (
        <Alert variant="danger" className="my-5">
          {wordsError}
        </Alert>
      )}
      {wordsData.words.length > 0 && (<div className="actions-panel my-3">
        <div className="actions-panel__intro mb-2">
          <div className="actions-panel__hint-title">{primaryCtaTitle}</div>
          <div className="actions-panel__hint">{primaryCtaHint}</div>
        </div>
        <Button variant="primary" className="d-flex w-100 mb-2 progress-action-btn progress-action-btn--primary" onClick={handlePrimaryAction}>
          <span className="progress-action-btn__content">
            <i className="ic ic-cards" />
            <span>{primaryCtaLabel}</span>
          </span>
        </Button>
        {hasUnstudiedCards && !previewPending && (
          <Button variant="outline-secondary" className="d-flex w-100 mb-2 progress-action-btn" onClick={handleUnstudiedCardsOpen}>
            <span className="progress-action-btn__content">
              <i className="ic ic-arrow-left" />
              <span>Repeat unstudied</span>
            </span>
          </Button>
        )}
        {hasRememberedCards && !previewPending && (
          <Button variant="outline-secondary" className="d-flex w-100 mb-2 progress-action-btn" onClick={handleRememberedCardsOpen}>
            <span className="progress-action-btn__content">
              <i className="ic ic-restore" />
              <span>Repeat remembered</span>
            </span>
          </Button>
        )}
        {previewPending && (
          <div className="preview-hydration-note mb-2">
            <div className="preview-hydration-note__title">
              Quick start: {previewReady} / {previewTarget || 30} words are ready
            </div>
            <div className="preview-hydration-note__text">
              Loading remaining {Math.max(0, (previewTarget || 30) - previewReady)} words in background
            </div>
            <div className="preview-hydration-note__status">
              <Spinner animation="border" size="sm" role="status" aria-hidden="true" />
              <span>Please wait, your full batch will appear automatically</span>
            </div>
          </div>
        )}
        <Button variant="outline-secondary" className="d-flex w-100 mb-1 progress-action-btn" onClick={handleListOpen} disabled={previewPending}>
          <span className="progress-action-btn__content">
            <i className="ic ic-long-list" />
            <span>List of words</span>
          </span>
        </Button>
      </div>)}
      {wordsData.words.length > 0 && (
        <div className="episode-progress-panel border rounded p-3 mt-3">
          {isEpisodeProgressLoading ? (
            <div className="episode-progress-skeleton" aria-hidden="true">
              <div className="episode-progress-header mb-1">
                <div className="episode-progress-title skeleton-line skeleton-line--title" />
                <div className="episode-progress-subtitle skeleton-line skeleton-line--subtitle" />
              </div>
              <div className="episode-progress-visual mt-2">
                <div className="episode-progress-chips">
                  <span className="progress-chip skeleton-chip" />
                  <span className="progress-chip skeleton-chip" />
                </div>
                <div className="episode-progress-chart-row">
                  <div className="episode-progress-donut skeleton-donut" />
                  <div className="episode-progress-legend">
                    <div className="legend-item skeleton-legend-item">
                      <span className="legend-dot skeleton-dot" />
                      <span className="skeleton-line skeleton-line--legend" />
                      <strong className="skeleton-line skeleton-line--percent" />
                    </div>
                    <div className="legend-item skeleton-legend-item">
                      <span className="legend-dot skeleton-dot" />
                      <span className="skeleton-line skeleton-line--legend" />
                      <strong className="skeleton-line skeleton-line--percent" />
                    </div>
                    <div className="legend-item skeleton-legend-item">
                      <span className="legend-dot skeleton-dot" />
                      <span className="skeleton-line skeleton-line--legend" />
                      <strong className="skeleton-line skeleton-line--percent" />
                    </div>
                  </div>
                </div>
                <div className="episode-progress-summary mt-2 skeleton-summary">
                  <div className="skeleton-line skeleton-line--summary-lg" />
                  <div className="skeleton-line skeleton-line--summary-sm" />
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="episode-progress-header mb-1">
                <div className="episode-progress-title">Progress overview</div>
                <div className="episode-progress-subtitle">Current batch status</div>
              </div>
              <div className="episode-progress-visual mt-2">
                <div className="episode-progress-chips">
                  <span className={`progress-chip ${progressStatusClass}`}>{progressStatusLabel}</span>
                  {hasMoreWords && <span className="progress-chip progress-chip--neutral">More words available</span>}
                </div>
                <div className="episode-progress-chart-row">
                  <div
                    className="episode-progress-donut"
                    aria-label={`Progress chart. Learned ${episodeCompletionPercent} percent, review ${episodeReviewPercent} percent, unseen ${episodeUnseenPercent} percent.`}
                    style={{
                      background: `conic-gradient(
                        #1c8f3f 0% ${episodeCompletionPercent}%,
                        #cc7a1f ${episodeCompletionPercent}% ${episodeCompletionPercent + episodeReviewPercent}%,
                        #c8d0dc ${episodeCompletionPercent + episodeReviewPercent}% 100%
                      )`
                    }}
                  >
                    <span className="episode-progress-donut__center">
                      <strong>{episodeRemembered}</strong>
                      <small>of {episodeTotal}</small>
                    </span>
                  </div>
                  <div className="episode-progress-legend">
                    <div className="legend-item">
                      <span className="legend-dot legend-dot--learned" />
                      <span>Learned</span>
                      <strong>{episodeCompletionPercent}%</strong>
                    </div>
                    <div className="legend-item">
                      <span className="legend-dot legend-dot--review" />
                      <span>Review</span>
                      <strong>{episodeReviewPercent}%</strong>
                    </div>
                    <div className="legend-item">
                      <span className="legend-dot legend-dot--unseen" />
                      <span>Unseen</span>
                      <strong>{episodeUnseenPercent}%</strong>
                    </div>
                  </div>
                </div>
              </div>
              {allWordsLearned && (
                <div className="episode-progress-summary episode-progress-summary--success mt-2">
                  <div className="fw-semibold">Great job, all words are learned.</div>
                  <div>
                    You can watch this {materialTypeLabel} now
                    {hasMoreWords ? ', or load more words to prepare even better.' : '.'}
                  </div>
                  {hasMoreWords && (
                    <Button
                      variant="outline-success"
                      size="sm"
                      className="mt-2"
                      onClick={handleListOpen}
                    >
                      Open word list
                    </Button>
                  )}
                </div>
              )}
              {!allWordsLearned && allWordsReviewed && (
                <div className="episode-progress-summary episode-progress-summary--warning mt-2">
                  <div className="fw-semibold">You have reviewed all words in this batch.</div>
                  <div>
                    {episodeNotRemembered} {episodeNotRemembered === 1 ? 'word is' : 'words are'} still not learned.
                    Use repeat mode to lock them in.
                  </div>
                </div>
              )}
              {!allWordsReviewed && (
                <div className="episode-progress-summary episode-progress-summary--info mt-2">
                  <div className="fw-semibold">
                    Keep going: {episodeUnseen} {episodeUnseen === 1 ? 'word' : 'words'} left in this batch.
                  </div>
                  {hasMoreWords && (
                    <div>
                      After this batch, you can load more words from the full timeline.
                    </div>
                  )}
                </div>
              )}
              {(hasMoreWords || hasEpisodeProgress || !!selectedMedia) && (
                <div className="actions-panel__utility-links mt-2">
                  <div className="actions-panel__utility-group">
                    {effectiveLoadedCardsTotal > 30 && (
                      <>
                        <Button
                          variant="link"
                          className={`actions-panel__utility-link ${effectiveActiveCardsLimit <= 30 ? 'actions-panel__utility-link--active' : ''}`}
                          onClick={() => handleSetActiveCardsLimit(30)}
                        >
                          Study 30
                        </Button>
                        <Button
                          variant="link"
                          className={`actions-panel__utility-link ${effectiveActiveCardsLimit > 30 ? 'actions-panel__utility-link--active' : ''}`}
                          onClick={() => handleSetActiveCardsLimit(effectiveLoadedCardsTotal)}
                        >
                          Study all loaded ({effectiveLoadedCardsTotal})
                        </Button>
                      </>
                    )}
                  </div>
                  <div className="actions-panel__utility-group actions-panel__utility-group--right">
                    {showLoadMoreButton && (
                      <Button
                        variant="link"
                        className="actions-panel__utility-link"
                        onClick={handleLoadMoreWords}
                        disabled={previewPending || wordsLoading || isBackgroundChunkLoading || !hasMoreWords}
                      >
                        Load more words
                      </Button>
                    )}
                    {selectedMedia && (
                      <Button
                        variant="link"
                        className="actions-panel__utility-link actions-panel__utility-link--danger"
                        onClick={() => setShowResetConfirm(true)}
                        disabled={isResettingProgress || !hasEpisodeProgress}
                        title={!hasEpisodeProgress ? 'Start learning first to enable reset' : undefined}
                      >
                        {isResettingProgress ? 'Resetting...' : 'Reset progress'}
                      </Button>
                    )}
                  </div>
                </div>
              )}
              {isBackgroundChunkLoading && (
                <div className="chunk-loading-note mt-2" role="status" aria-live="polite">
                  <Spinner animation="border" size="sm" role="status" aria-hidden="true" />
                  <div>
                    <div className="chunk-loading-note__title">Loading more words in background</div>
                    <div className="chunk-loading-note__text">{compactChunkLoadingLabel}. You can continue current cards.</div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
      {showCards && (<FlashcardView onClose={handleCardsClose} mode={flashcardMode} />)}
      {showWordsList && (<WordsListView wordsData={wordsData.words} loadMoreEnabled={canLoadMoreWords(wordsData)} onClose={handleWordsListClose} />)}
      <Modal show={showResetConfirm} onHide={() => setShowResetConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Reset progress?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          This will clear your current card statuses and study position for this material.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowResetConfirm(false)} disabled={isResettingProgress}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleResetProgress} disabled={isResettingProgress}>
            {isResettingProgress ? 'Resetting...' : 'Reset progress'}
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal show={showLoadMoreConfirm} onHide={() => setShowLoadMoreConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Load more words?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          This will start loading extra words from the full timeline. It can take 1-2 minutes, and during this time
          loading new materials will be locked.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowLoadMoreConfirm(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConfirmLoadMoreWords}>
            Load more
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SearchPage;
